// ── CHAT MESSAGE RENDERERS (fp-nofodesk-9) — extracted from App.js ──
import { useState } from "react";
import { LogoMark } from "../LandingPage";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";
import MarkdownText from "./MarkdownText";
import ExtractionTable from "./ExtractionTable";

// ── MessageBubble ─────────────────────────────────────────────
const AI_AVATAR = (
  <div style={{ width:30, height:30, borderRadius:"50%", background:"rgba(91,79,233,0.12)", border:"1px solid rgba(91,79,233,0.35)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:2 }}>
    <LogoMark size={15} />
  </div>
);

// Staged extraction progress — one threshold table drives both the header
// label and the done/active state of each stage chip
const PROGRESS_STAGES = [
  { label: "Reading document",  min: 0  },
  { label: "Extracting fields", min: 35 },
  { label: "Formatting",        min: 80 },
];
function progressStage(percent) {
  if (percent == null) return "Working…";
  let current = PROGRESS_STAGES[0].label;
  for (const s of PROGRESS_STAGES) if (percent >= s.min) current = s.label;
  return current;
}
// Inline 4-field mini-profile (ONBOARDING_SPEC Beat 3) — never gates anything
function MiniProfileForm({ T, onSave }) {
  const [name,    setName]    = useState("");
  const [type,    setType]    = useState("NGO");
  const [country, setCountry] = useState("");
  const [focus,   setFocus]   = useState("");
  const [saved,   setSaved]   = useState(false);
  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "8px 10px", background: T.bgSurface,
    border: `1px solid ${T.border}`, borderRadius: 7, color: T.textBody,
    fontSize: 13, fontFamily: FONT_UI, outline: "none",
  };
  if (saved) {
    return (
      <div style={{ fontSize: 13, color: TOKENS.statusGreen, fontFamily: FONT_UI }}>
        ✓ Profile saved — the eligibility checker now works on every extraction. Refine it any time in Settings.
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 13, color: T.text, fontFamily: FONT_UI, fontWeight: 600, marginBottom: 4 }}>
        Want to know if you're eligible?
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT_UI, lineHeight: 1.5, marginBottom: 12 }}>
        Set up your org profile — 30 seconds. It unlocks the eligibility checker on every extraction.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Organisation name" style={{ ...inputStyle, gridColumn: "1 / -1" }} />
        <select value={type} onChange={e => setType(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
          {["NGO", "INGO", "University / Research", "Social enterprise", "Government", "Other"].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={country} onChange={e => setCountry(e.target.value)} placeholder="Country" style={inputStyle} />
        <input value={focus} onChange={e => setFocus(e.target.value)} placeholder="Focus areas (comma-separated, e.g. Health, WASH)" style={{ ...inputStyle, gridColumn: "1 / -1" }} />
      </div>
      <button
        disabled={!name.trim()}
        onClick={() => {
          onSave({ name: name.trim(), type, country: country.trim(), focusAreas: focus.split(",").map(s => s.trim()).filter(Boolean) });
          setSaved(true);
        }}
        style={{ padding: "8px 18px", background: name.trim() ? TOKENS.gold : T.bgElevated, color: name.trim() ? "#fff" : T.textMuted, border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, fontFamily: FONT_UI, cursor: name.trim() ? "pointer" : "not-allowed", minHeight: 36 }}>
        Save profile
      </button>
    </div>
  );
}

function MessageBubble({ msg, orgProfile, onAddToPipeline, onMiniProfileSave, onDeadlineSave }) {
  const T = useT();
  // Bubble colours. Dark and black modes keep exactly the colours they always
  // had; light mode gets its own, because navy bubbles and pale text on a
  // white page were the same disparity Discover had.
  const L = T.mode === "light";
  const P = L ? {
    userBg: "#EEECFD", userBorder: "#D6D1FA", userText: "#1F1B3A", fileIconBg: "#FFFFFF", fileMeta: "#6E6A8A",
    restTitle: "#5B4FE9", restBody: "#4B4870", restStrong: "#2E2A6B", restMeta: "#75718A",
    hintBg: "#FFFFFF", hintTitle: "#B45309", hintText: "#7C4A03",
    limitBg: "#F5F3FF", limitText: "#4338CA", limitStrong: "#1F1B3A", limitSub: "#5B4FE9",
    errBg: T.conflictBg || "#FDECEC", errText: "#991B1B",
  } : {
    userBg: "#1d2d4e", userBorder: "#2d4270", userText: "#e2e8f4", fileIconBg: "#0f172a", fileMeta: "#7a95bb",
    restTitle: "#a78bfa", restBody: "#8b9cf4", restStrong: "#c4b5fd", restMeta: "#6b7280",
    hintBg: "#0D0E20", hintTitle: "#f59e0b", hintText: "#fcd34d",
    limitBg: "#0d0a1f", limitText: "#c4b5fd", limitStrong: "#fff", limitSub: "#a5b4fc",
    errBg: "#1a0808", errText: "#fca5a5",
  };
  if (msg.role === "user" && msg.type === "file") {
    return (
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
        <div style={{ maxWidth:"72%", background:P.userBg, border:`1px solid ${P.userBorder}`, borderRadius:"16px 4px 16px 16px", padding:"10px 14px", display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:8, background:P.fileIconBg, border:`1px solid ${P.userBorder}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>📄</div>
          <div>
            <div style={{ fontSize:13, color:P.userText, fontWeight:600, fontFamily:FONT_UI }}>{msg.fileName}</div>
            <div style={{ fontSize:11, color:P.fileMeta, marginTop:2, fontFamily:FONT_UI }}>{msg.fileSize} · PDF document</div>
          </div>
        </div>
      </div>
    );
  }
  if (msg.role === "user") {
    return (
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
        <div style={{ maxWidth:"72%", background:P.userBg, color:P.userText, padding:"11px 16px", borderRadius:"16px 4px 16px 16px", fontSize:14, lineHeight:1.6, wordBreak:"break-word", border:`1px solid ${P.userBorder}` }}>
          {msg.text}
        </div>
      </div>
    );
  }
  if (msg.type === "session_restored") {
    const src  = msg.grantSource || "";
    const when = msg.updatedAt ? new Date(msg.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
    // Label priority: extracted grant title > URL > first words of the source.
    // Everything is hard-truncated: for text extractions _source holds the FULL
    // pasted document, so an untruncated label dumps the whole NOFO in here.
    const clip  = (s, n) => (s.length > n ? s.slice(0, n).trimEnd() + "…" : s);
    const title = (msg.grantTitle || "").trim();
    const label = title                  ? clip(title, 80)
                : src.startsWith("http") ? clip(src, 70)
                : src.trim()             ? clip(src.replace(/\s+/g, " ").trim(), 80)
                : "your previous session";
    return (
      <div style={{ display:"flex", justifyContent:"flex-start", gap:10, marginBottom:16, alignItems:"flex-start" }}>
        {AI_AVATAR}
        <div style={{ maxWidth:"86%", background:"rgba(91,79,233,0.08)", border:"1px solid rgba(91,79,233,0.3)", borderRadius:"4px 16px 16px 16px", padding:"10px 16px", display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:16, flexShrink:0 }}>🔄</span>
          <div>
            <div style={{ fontFamily:FONT_UI, fontSize:12, fontWeight:700, color:P.restTitle, marginBottom:2 }}>Session restored</div>
            <div style={{ fontFamily:FONT_UI, fontSize:12, color:P.restBody, lineHeight:1.5 }}>
              Continuing your previous conversation about <strong style={{ color:P.restStrong }}>{label}</strong>
              {when && <span style={{ color:P.restMeta }}> · last active {when}</span>}.
              {" "}You can ask follow-up questions or paste a new grant URL to start fresh.
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (msg.type === "hint") {
    const steps  = Array.isArray(msg.steps)  ? msg.steps  : [];
    const reason = msg.reason || msg.text || '';
    return (
      <div style={{ display:"flex", justifyContent:"flex-start", gap:10, marginBottom:16, alignItems:"flex-start" }}>
        {AI_AVATAR}
        <div style={{ maxWidth:"86%", background:P.hintBg, border:"1px solid #f59e0b", borderRadius:"4px 16px 16px 16px", overflow:"hidden" }}>
          <div style={{ padding:"10px 14px 8px", background:"rgba(245,158,11,0.1)", borderBottom:"1px solid rgba(245,158,11,0.2)", display:"flex", alignItems:"center", gap:7 }}>
            <span style={{ fontSize:14 }}>⚠️</span>
            <span style={{ fontFamily:FONT_UI, fontSize:12, fontWeight:700, color:P.hintTitle, textTransform:"uppercase", letterSpacing:"0.06em" }}>Could not extract from this URL</span>
          </div>
          <div style={{ padding:"12px 16px" }}>
            {reason && (
              <div style={{ fontFamily:FONT_UI, fontSize:13, color:P.hintText, lineHeight:1.65, marginBottom: steps.length ? 12 : 0 }}>
                {reason}
              </div>
            )}
            {steps.length > 0 && (
              <>
                <div style={{ fontFamily:FONT_UI, fontSize:11, fontWeight:700, color:P.hintTitle, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>What to do instead</div>
                {steps.map((s, i) => (
                  <div key={i} style={{ display:"flex", gap:10, marginBottom:7, alignItems:"flex-start" }}>
                    <div style={{ width:20, height:20, borderRadius:"50%", background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.4)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:11, fontWeight:700, color:P.hintTitle, fontFamily:FONT_UI }}>{i+1}</div>
                    <span style={{ fontFamily:FONT_UI, fontSize:13, color:P.hintText, lineHeight:1.55, paddingTop:2 }}>{s}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (msg.type === "extraction_limit") {
    return (
      <div style={{ display:"flex", justifyContent:"flex-start", gap:10, marginBottom:16, alignItems:"flex-start" }}>
        {AI_AVATAR}
        <div style={{ maxWidth:"82%", background:P.limitBg, border:"1px solid #5B4FE9", color:P.limitText, padding:"16px 20px", borderRadius:"4px 16px 16px 16px", fontSize:13, lineHeight:1.7 }}>
          <div style={{ fontWeight:700, color:P.limitStrong, marginBottom:8 }}>You have used all 5 free extractions this month.</div>
          <div style={{ marginBottom:10, color:P.limitSub }}>Upgrade to Professional — <strong style={{ color:P.limitStrong }}>$49/month</strong></div>
          {["50 extractions/month","AI Proposal Writing","All 6 donor schemas","Eligibility Checker","Google Drive + OneDrive sync","Excel and PowerPoint export"].map(f => (
            <div key={f} style={{ fontSize:12, color:P.limitText, marginBottom:4 }}>✓ {f}</div>
          ))}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("nofodesk:open-pricing"))}
            style={{ marginTop:14, padding:"8px 20px", background:"#5B4FE9", color:"#fff", border:"none", borderRadius:7, fontWeight:700, fontSize:13, cursor:"pointer" }}
          >Upgrade Now</button>
        </div>
      </div>
    );
  }
  if (msg.type === "error") {
    return (
      <div style={{ display:"flex", justifyContent:"flex-start", gap:10, marginBottom:16, alignItems:"flex-start" }}>
        {AI_AVATAR}
        <div style={{ maxWidth:"82%", background:P.errBg, border:"1px solid #ef4444", color:P.errText, padding:"12px 16px", borderRadius:"4px 16px 16px 16px", fontSize:13, lineHeight:1.6 }}>
          <strong>Error:</strong> {msg.text}
        </div>
      </div>
    );
  }
  if (msg.type === "progress") {
    const pct = Math.max(0, Math.min(100, msg.percent || 0));
    return (
      <div style={{ display:"flex", justifyContent:"flex-start", gap:10, marginBottom:16, alignItems:"flex-start" }}>
        {AI_AVATAR}
        <div style={{ background:T.bgElevated, border:`1px solid ${T.border}`, padding:"14px 18px", borderRadius:"4px 16px 16px 16px", minWidth:260, maxWidth:"78%" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:12, marginBottom:8 }}>
            <span style={{ fontSize:12, color:T.textSub, fontFamily:FONT_UI, fontWeight:600 }}>{msg.message || progressStage(msg.percent)}</span>
            <span style={{ fontSize:11, color:T.textMuted, fontFamily:FONT_MONO, fontVariantNumeric:"tabular-nums" }}>{pct}%</span>
          </div>
          <div style={{ height:3, background:T.border, borderRadius:2, overflow:"hidden" }} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div style={{ height:"100%", width:`${pct}%`, background:TOKENS.gold, transition:"width 0.4s ease" }} />
          </div>
          <div style={{ display:"flex", gap:10, marginTop:8 }}>
            {PROGRESS_STAGES.map((stage, i) => {
              const nextMin = PROGRESS_STAGES[i + 1]?.min ?? 100;
              const done   = pct >= nextMin;
              const active = !done && pct >= stage.min;
              return (
                <span key={stage.label} style={{ fontSize:10, fontFamily:FONT_MONO, letterSpacing:"0.04em", color: done ? TOKENS.statusGreen : active ? T.textSub : T.textFaint, transition:"color 0.3s" }}>
                  {done ? "✓ " : ""}{stage.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
  if (msg.type === "extraction") {
    return (
      <div style={{ display:"flex", justifyContent:"flex-start", gap:10, marginBottom:16, alignItems:"flex-start" }}>
        {AI_AVATAR}
        <div style={{ maxWidth:"97%", width:"100%" }}>
          <div style={{ border:`1px solid ${T.border}`, borderRadius:14, overflow:"hidden", boxShadow:TOKENS.shadowElevated }}>
            <ExtractionTable data={msg.data} orgProfile={orgProfile} onAddToPipeline={onAddToPipeline} />
          </div>
          {msg.isSample && (
            <div style={{ marginTop:8, padding:"10px 14px", background:"rgba(91,79,233,0.08)", border:"1px solid rgba(91,79,233,0.3)", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
              <span style={{ fontSize:12, color:T.textSub, fontFamily:FONT_UI }}>
                This is a sample. Run it on a grant you're actually working on →
              </span>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("nofodesk:focus-input"))}
                style={{ padding:"6px 14px", background:TOKENS.gold, color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:600, fontFamily:FONT_UI, cursor:"pointer", minHeight:32 }}>
                Paste your grant
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (msg.type === "profile_prompt") {
    return (
      <div style={{ display:"flex", justifyContent:"flex-start", gap:10, marginBottom:16, alignItems:"flex-start" }}>
        {AI_AVATAR}
        <div style={{ maxWidth:"78%", minWidth:280, background:T.bgElevated, border:`1px solid ${T.border}`, borderRadius:"4px 16px 16px 16px", padding:"14px 16px" }}>
          <MiniProfileForm T={T} onSave={onMiniProfileSave} />
        </div>
      </div>
    );
  }
  if (msg.type === "deadline_prompt") {
    const dl = msg.data?.timeline?.applicationDeadline;
    return (
      <div style={{ display:"flex", justifyContent:"flex-start", gap:10, marginBottom:16, alignItems:"flex-start" }}>
        {AI_AVATAR}
        <div style={{ maxWidth:"78%", background:T.bgElevated, border:`1px solid ${T.border}`, borderRadius:"4px 16px 16px 16px", padding:"12px 16px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          {msg.saved ? (
            <span style={{ fontSize:13, color:TOKENS.statusGreen, fontFamily:FONT_UI }}>
              ✓ Deadline saved{msg.daysLeft != null ? ` — ${msg.daysLeft} days out` : ""}. It's in your tracker.
            </span>
          ) : (
            <>
              <span style={{ fontSize:13, color:T.textBody, fontFamily:FONT_UI }}>
                Save this deadline to your tracker{dl && dl !== "Not specified" ? <> — <span style={{ fontFamily:FONT_MONO, color:T.textSub }}>{dl}</span></> : ""}?
              </span>
              <button
                onClick={() => onDeadlineSave(msg.id, msg.data)}
                style={{ padding:"6px 14px", background:TOKENS.gold, color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:600, fontFamily:FONT_UI, cursor:"pointer", minHeight:32 }}>
                Save deadline
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
  const hasSources = Array.isArray(msg.sources) && msg.sources.length > 0;
  const isStreaming = msg.type === "streaming";
  return (
    <div style={{ display:"flex", justifyContent:"flex-start", gap:10, marginBottom:16, alignItems:"flex-start" }}>
      {AI_AVATAR}
      <div style={{ maxWidth:"78%" }}>
        <div style={{ background:T.bgElevated, border:`1px solid ${T.border}`, color:T.textBody, padding:"12px 16px", borderRadius:"4px 16px 16px 16px", fontSize:14, lineHeight:1.7, fontFamily:FONT_H }}>
          {isStreaming && !msg.text ? (
            <span style={{ animation:"pulse 1.5s infinite", display:"inline-block", fontSize:13, color:T.textMuted, fontFamily:FONT_UI }}>Preparing…</span>
          ) : (
            <>
              <MarkdownText text={msg.text} />
              {isStreaming && <span style={{ display:"inline-block", width:8, height:14, background:T.textBody, borderRadius:2, marginLeft:2, animation:"blink 1s step-end infinite", opacity:0.7 }} />}
            </>
          )}
        </div>
        {hasSources && (
          <div style={{ marginTop:8, padding:"8px 12px", background:T.bgApp, border:`1px solid ${T.borderDim}`, borderRadius:8, fontSize:11, color:T.textMuted }}>
            <div style={{ fontWeight:600, marginBottom:5, color:T.textFaint, letterSpacing:"0.05em", textTransform:"uppercase" }}>Research Sources</div>
            {msg.sources.map((s, i) => (
              <div key={i} style={{ marginBottom:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color:T.slateBlue || "#60a5fa", textDecoration:"none" }}>
                  {s.title || s.url}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MessageBubble;
